// mui
import {
  Avatar,
  Box,
  Card,
  Checkbox,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// components
import { Icon } from '@/components/atoms/Icon/Icon';
import { getProfilePictureUrl } from '@/pwa/utils/getProfilePictureUrl';

// types
import { OrganiseEntryStatus, OrganiseMember } from '@karehero/models';

export interface OrganiserEntryItem {
  id: string;
  calendarId: string;
  title: string;
  status: OrganiseEntryStatus;
  assigneeName?: string;
  assigneeProfile?: OrganiseMember;
  startTime?: string | null;
  endTime?: string | null;
  link?: string | null;
  careCircleColor: string;
  careCircleName: string;
  recurringEventId?: string | null;
  recurrenceDescription?: string | null;
  occurrenceDate?: string | null;
  /** Creator display name — shown as "From {createdByName}" in the member variant. */
  createdByName?: string | null;
}

const formatTime = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString([], {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
};

/**
 * The member-variant meta line under the title, e.g. "Every day · 14:00"
 * (recurrence + time), a dated label, or null when the entry has no schedule.
 */
const getMemberMetaLabel = (entry: OrganiserEntryItem): string | null => {
  if (entry.recurrenceDescription && entry.startTime) {
    return `${entry.recurrenceDescription} · ${formatTime(entry.startTime)}`;
  }
  if (entry.recurrenceDescription) {
    return entry.recurrenceDescription;
  }
  if (entry.startTime) {
    return `${formatDate(entry.startTime)} · ${formatTime(entry.startTime)}`;
  }
  if (entry.occurrenceDate) {
    return formatDate(entry.occurrenceDate);
  }
  return null;
};

interface OrganiserEntryRowProps {
  entry: OrganiserEntryItem;
  onStatusChange: (entryId: string, status: OrganiseEntryStatus) => void;
  onEntryClick: (entryId: string) => void;
  onEntryLinkClick?: (link: string) => void;
  isFading?: boolean;
  isOverdue?: boolean;
  /**
   * Presentation variant. `admin` (default) shows the care-circle dot+name;
   * `member` hides it and shows a "From {createdByName}" line instead.
   */
  variant?: 'admin' | 'member';
}

export const OrganiserEntryRow = ({
  entry,
  onStatusChange,
  onEntryClick,
  onEntryLinkClick,
  isFading,
  isOverdue,
  variant = 'admin',
}: OrganiserEntryRowProps) => {
  const theme = useTheme();
  const isCompleted = entry.status === OrganiseEntryStatus.Completed;
  const isWontDo = entry.status === OrganiseEntryStatus.WontDo;
  const hasDate = !!(entry.startTime || entry.occurrenceDate);
  const assigneeFallback = entry.assigneeName
    ? getInitials(entry.assigneeName)
    : 'U';
  const memberMetaLabel =
    variant === 'member' ? getMemberMetaLabel(entry) : null;

  return (
    <Card
      elevation={0}
      onClick={() => onEntryClick(entry.id)}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        bgcolor: 'background.paper',
        cursor: 'pointer',
        transition: 'background-color 0.15s, opacity 0.6s ease',
        opacity: isFading ? 0.4 : 1,
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      <Stack
        direction='row'
        spacing={3}
        sx={{
          alignItems: 'center',
          px: 2,
          py: 1.5,
        }}
      >
        <Checkbox
          checked={isCompleted}
          onChange={() => {
            onStatusChange(
              entry.id,
              isCompleted
                ? OrganiseEntryStatus.Open
                : OrganiseEntryStatus.Completed,
            );
          }}
          onClick={(e) => e.stopPropagation()}
          disableRipple
          sx={{ p: 0 }}
          icon={
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '6px',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: theme.palette.custom.marineGreen[900],
              }}
            />
          }
          checkedIcon={
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '6px',
                border: '1px solid',
                bgcolor: theme.palette.custom.marineGreen[900],
                borderColor: theme.palette.custom.marineGreen[900],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'background.paper',
              }}
            >
              <Icon icon='check' fontSize={12} />
            </Box>
          }
        />

        <Stack
          direction='column'
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <Stack
            direction='row'
            spacing={0.75}
            sx={{
              alignItems: 'center',
            }}
          >
            <Typography
              variant='subtitle2'
              sx={{
                textDecoration: isCompleted ? 'line-through' : 'none',
                opacity: isWontDo ? 0.5 : 1,
              }}
              noWrap
            >
              {entry.title.charAt(0).toUpperCase() + entry.title.slice(1)}
            </Typography>
          </Stack>
          <Stack
            sx={{
              gap: 0.25,
            }}
          >
            {variant === 'admin' && (
              <Stack
                direction='row'
                sx={{
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: entry.careCircleColor,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant='body2'
                  noWrap
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {entry.careCircleName}
                </Typography>
              </Stack>
            )}
            {variant === 'admin' && (hasDate || entry.recurringEventId) && (
              <Typography
                variant='body2'
                color={isOverdue ? 'error.main' : 'text.secondary'}
              >
                {entry.recurringEventId
                  ? [
                      'Repeats every',
                      entry.startTime
                        ? `${new Date(entry.startTime).toLocaleDateString([], { weekday: 'short' })} at ${formatTime(entry.startTime)}`
                        : entry.occurrenceDate
                          ? new Date(entry.occurrenceDate).toLocaleDateString(
                              [],
                              { weekday: 'short' },
                            )
                          : null,
                    ]
                      .filter(Boolean)
                      .join(' ')
                  : entry.startTime
                    ? `${formatDate(entry.startTime)} ${formatTime(entry.startTime)}`
                    : formatDate(entry.occurrenceDate!)}
              </Typography>
            )}
            {variant === 'member' && memberMetaLabel && (
              <Typography
                variant='body2'
                color={isOverdue ? 'error.main' : 'text.secondary'}
              >
                {memberMetaLabel}
              </Typography>
            )}
            {variant === 'member' && entry.createdByName && (
              <Typography
                variant='body2'
                noWrap
                sx={{
                  color: 'text.secondary',
                }}
              >
                From {entry.createdByName}
              </Typography>
            )}
          </Stack>
        </Stack>

        <Stack
          direction='row'
          spacing={0.75}
          sx={{
            alignItems: 'center',
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <Tooltip
            title={entry.assigneeName || 'Unassigned'}
            arrow
            placement='top'
          >
            <Box
              sx={{
                display: 'inline-flex',
              }}
            >
              {entry.assigneeName ? (
                <Avatar
                  alt={entry.assigneeName}
                  src={getProfilePictureUrl(entry.assigneeProfile)}
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: 12,
                  }}
                >
                  {assigneeFallback}
                </Avatar>
              ) : (
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: 'action.disabledBackground',
                    color: 'action.disabled',
                  }}
                >
                  <Icon icon='user' fontSize={14} />
                </Avatar>
              )}
            </Box>
          </Tooltip>
        </Stack>

        {entry.link && onEntryLinkClick && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onEntryLinkClick(entry.link!);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              ml: 0.5,
              cursor: 'pointer',
              '&:hover': { color: 'text.primary' },
            }}
          >
            <Icon icon='chevron-right' fontSize={16} />
          </Box>
        )}
      </Stack>
    </Card>
  );
};
